import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DollarSign, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useThemeStore } from '../../stores/themeStore';
import { FinancialSummary } from '../../types/adminCalendar';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AdminFinancials: React.FC = () => {
  const isDark = useThemeStore((s) => s.theme) === 'dark';
  const [year, setYear] = useState(2026);

  const { data, isLoading, error } = useQuery<FinancialSummary>({
    queryKey: ['adminFinancials', year],
    queryFn: () => adminService.getFinancials(year),
    staleTime: 120_000,
  });

  const borderCls = isDark ? 'border-white/10' : 'border-slate-200';

  if (isLoading) {
    return (
      <div className={`backdrop-blur-sm rounded-2xl border p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="animate-pulse space-y-4">
          <div className="flex gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className={`flex-1 h-20 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-100'}`} />
            ))}
          </div>
          <div className={`h-64 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-100'}`} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`backdrop-blur-sm rounded-2xl border p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-sm text-red-400">Failed to load financial data</p>
        </div>
      </div>
    );
  }

  const chartData = {
    labels: data.monthly.map(m => m.month),
    datasets: [
      {
        label: 'Earnings',
        data: data.monthly.map(m => m.earnings),
        backgroundColor: isDark ? 'rgba(34, 197, 94, 0.6)' : 'rgba(34, 197, 94, 0.7)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false as const,
      },
      {
        label: 'Expenses',
        data: data.monthly.map(m => m.expenses),
        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.5)' : 'rgba(239, 68, 68, 0.6)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false as const,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 3,
          useBorderRadius: true,
          font: { size: 11 },
          color: isDark ? '#94a3b8' : '#64748b',
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        titleColor: isDark ? '#e2e8f0' : '#1e293b',
        bodyColor: isDark ? '#94a3b8' : '#64748b',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: (ctx: any) => ` ${ctx.dataset.label}: $${ctx.raw.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: isDark ? '#64748b' : '#94a3b8', font: { size: 11 } },
        border: { display: false },
      },
      y: {
        grid: { color: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' },
        ticks: {
          color: isDark ? '#64748b' : '#94a3b8',
          font: { size: 11 },
          callback: (v: any) => `$${v >= 1000 ? `${v / 1000}k` : v}`,
        },
        border: { display: false },
      },
    },
    interaction: { intersect: false, mode: 'index' as const },
    barPercentage: 0.7,
    categoryPercentage: 0.8,
  };

  const summaryCards = [
    {
      label: 'Total Earnings',
      value: data.total_earnings,
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-green-500',
      iconBg: isDark ? 'bg-emerald-500/20' : 'bg-emerald-100',
      iconColor: isDark ? 'text-emerald-400' : 'text-emerald-600',
    },
    {
      label: 'Total Expenses',
      value: data.total_expenses,
      icon: TrendingDown,
      gradient: 'from-red-500 to-rose-500',
      iconBg: isDark ? 'bg-red-500/20' : 'bg-red-100',
      iconColor: isDark ? 'text-red-400' : 'text-red-600',
    },
    {
      label: 'Net Profit',
      value: data.net_profit,
      icon: DollarSign,
      gradient: 'from-cyan-500 to-blue-500',
      iconBg: isDark ? 'bg-cyan-500/20' : 'bg-cyan-100',
      iconColor: isDark ? 'text-cyan-400' : 'text-cyan-600',
    },
  ];

  return (
    <div className={`backdrop-blur-sm rounded-2xl border overflow-hidden transition-colors ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`p-5 border-b ${borderCls}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Financial Overview</h3>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Monthly earnings & expenses</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setYear(y => y - 1)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className={`text-sm font-semibold min-w-[50px] text-center ${isDark ? 'text-white' : 'text-slate-800'}`}>{year}</span>
            <button onClick={() => setYear(y => y + 1)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 p-5">
        {summaryCards.map(({ label, value, icon: Icon, iconBg, iconColor }) => (
          <div key={label} className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center`}>
                <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
              </div>
              <span className={`text-[11px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-slate-800'}`}>
              ${value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="px-5 pb-5">
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50/50 border-slate-100'}`}>
          <div className="h-72">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFinancials;
